/**
 * 虚拟鼠标事件系统
 * 在全屏模式下，根据 Canvas 绘制的鼠标位置，向对应位置的元素派发合成鼠标事件
 */

export const VIRTUAL_MOUSE_MOVE_EVENT = "virtual-mousemove"
export const VIRTUAL_MOUSE_DOWN_EVENT = "virtual-mousedown"
export const VIRTUAL_MOUSE_UP_EVENT = "virtual-mouseup"

export interface VirtualMousePointerEventDetail {
  x: number
  y: number
  button: number
  buttons: number
  isOnUI: boolean
}

// 状态管理
let lastHoveredElement: Element | null = null
let lastHoveredUIContainer: Element | null = null // 记录添加了 virtual-hover class 的 UI 容器
let mouseDownElement: Element | null = null
let mouseDownUIContainer: Element | null = null // 记录 mousedown 时的 UI 容器
let mouseDownPosition: { x: number; y: number } | null = null // 记录 mousedown 时的坐标
let pressedButtons: number = 0
// 跟踪上一次是否在 UI 区域（用于检测进入/离开状态）
let wasOnUIArea: boolean = false
// 标记下一个原生 click 事件是否应该被阻止（在虚拟鼠标点击 UI 元素后设置）
let shouldBlockNextClick: boolean = false
// 当前虚拟鼠标位置（用于捕获阶段事件拦截）
let currentVirtualMousePosition: { x: number; y: number } | null = null
// 是否启用捕获阶段拦截（全屏模式时启用）
let captureInterceptionEnabled: boolean = false
// 捕获阶段事件处理回调（用于更新 Redux 状态等）
let capturePhaseCallback:
  | ((type: "down" | "up", x: number, y: number, button: number) => void)
  | null = null

// dispatchMove 的返回值类型
export interface DispatchMoveResult {
  isOnUI: boolean // 当前是否在 UI 元素上
  justEntered: boolean // 是否刚进入远程桌面（从 UI 区域离开到非 UI 区域）
  justLeave: boolean // 是否刚离开远程桌面（从非 UI 区域进入 UI 区域）
}

function dispatchVirtualPointerEvent(
  type: string,
  detail: VirtualMousePointerEventDetail
): void {
  document.dispatchEvent(
    new CustomEvent<VirtualMousePointerEventDetail>(type, {
      detail,
    })
  )
}

// 检查元素是否是 full-screen-unvideo-element-tag 的子元素或自身
function isFullScreenUIElement(el: Element): boolean {
  let current: Element | null = el
  while (current) {
    if (current.id === "full-screen-unvideo-element-tag") {
      return true
    }
    current = current.parentElement
  }
  return false
}

// 查找元素最近的 full-screen-unvideo-element-tag 祖先（包括自身）
function findUIContainer(el: Element): Element | null {
  let current: Element | null = el
  while (current) {
    if (current.id === "full-screen-unvideo-element-tag") {
      return current
    }
    current = current.parentElement
  }
  return null
}

// 查找虚拟鼠标位置下的 UI 元素（只返回 full-screen-unvideo-element-tag 内的元素）
function getElementAtPosition(x: number, y: number): Element | null {
  const elements = document.elementsFromPoint(x, y)

  for (const el of elements) {
    const tagName = el.tagName.toLowerCase()
    // 跳过 canvas（绘制的鼠标）和 video（云桌面）
    if (tagName === "canvas" || tagName === "video") {
      continue
    }
    // 跳过设置了 pointer-events: none 的元素
    const style = window.getComputedStyle(el)
    if (style.pointerEvents === "none") {
      continue
    }
    // 只返回 full-screen-unvideo-element-tag 内的元素
    if (isFullScreenUIElement(el)) {
      return el
    }
  }
  return null
}

// 创建合成鼠标事件
function createSyntheticMouseEvent(
  type: string,
  x: number,
  y: number,
  buttons: number = 0,
  button: number = 0
): MouseEvent {
  return new MouseEvent(type, {
    bubbles: true,
    cancelable: true,
    composed: true, // 允许事件穿越 Shadow DOM 边界和组件边界
    view: window,
    clientX: x,
    clientY: y,
    screenX: x,
    screenY: y,
    button,
    buttons,
    relatedTarget: null,
  })
}

// 捕获阶段 mousedown/pointerdown 事件处理器
// 在 useOutsideClick 等 document 级别监听器之前拦截事件
function handleCapturePhaseMouseDown(event: MouseEvent | PointerEvent): void {
  if (!captureInterceptionEnabled || !currentVirtualMousePosition) {
    return
  }
  // 只处理来自 video 元素的事件
  const target = event.target as HTMLElement
  if (target.id !== "remote-desk-video") {
    return
  }
  const { x, y } = currentVirtualMousePosition
  const isOnUI = isPositionOnUIElement(x, y)
  if (isOnUI) {
    // 先调用回调更新外部状态（如 Redux）
    if (capturePhaseCallback) {
      capturePhaseCallback("down", x, y, event.button)
    }
    // 处理虚拟鼠标事件
    dispatchVirtualMouseDown(x, y, event.button)
    // 阻止事件传播，防止 useOutsideClick 等监听器接收到这个事件
    event.stopImmediatePropagation()
    event.preventDefault()
  }
}

// 捕获阶段 mouseup/pointerup 事件处理器
function handleCapturePhaseMouseUp(event: MouseEvent | PointerEvent): void {
  if (!captureInterceptionEnabled || !currentVirtualMousePosition) {
    return
  }
  // 只处理来自 video 元素的事件
  const target = event.target as HTMLElement
  if (target.id !== "remote-desk-video") {
    return
  }
  const { x, y } = currentVirtualMousePosition
  const isOnUI = isPositionOnUIElement(x, y)
  // 对于 mouseup，即使当前位置不在 UI 上，如果 mouseDownElement 存在，也需要处理
  // 这处理了用户在 UI 元素上按下但移动到非 UI 区域释放的情况
  if (isOnUI || mouseDownElement) {
    // 先调用回调更新外部状态（如 Redux）
    if (capturePhaseCallback) {
      capturePhaseCallback("up", x, y, event.button)
    }
    // 处理虚拟鼠标事件
    dispatchVirtualMouseUp(x, y, event.button)
    // 阻止事件传播
    event.stopImmediatePropagation()
    event.preventDefault()
  }
}

// 设置捕获阶段事件监听器
function setupCaptureListeners(
  onVirtualMouseEvent?: (
    type: "down" | "up",
    x: number,
    y: number,
    button: number
  ) => void
): () => void {
  capturePhaseCallback = onVirtualMouseEvent || null
  // 同时监听 mouse 和 pointer 事件（Chakra UI 的 useOutsideClick 可能使用 pointer 事件）
  document.addEventListener("mousedown", handleCapturePhaseMouseDown, {
    capture: true,
  })
  document.addEventListener("mouseup", handleCapturePhaseMouseUp, {
    capture: true,
  })
  document.addEventListener("pointerdown", handleCapturePhaseMouseDown, {
    capture: true,
  })
  document.addEventListener("pointerup", handleCapturePhaseMouseUp, {
    capture: true,
  })
  captureInterceptionEnabled = true

  return () => {
    document.removeEventListener("mousedown", handleCapturePhaseMouseDown, {
      capture: true,
    })
    document.removeEventListener("mouseup", handleCapturePhaseMouseUp, {
      capture: true,
    })
    document.removeEventListener("pointerdown", handleCapturePhaseMouseDown, {
      capture: true,
    })
    document.removeEventListener("pointerup", handleCapturePhaseMouseUp, {
      capture: true,
    })
    captureInterceptionEnabled = false
    capturePhaseCallback = null
    currentVirtualMousePosition = null
  }
}

// 更新当前虚拟鼠标位置（供捕获阶段拦截使用）
function updateVirtualMousePosition(x: number, y: number): void {
  currentVirtualMousePosition = { x, y }
}

// 派发虚拟鼠标 移动事件，返回是否作用在 UI 元素上及进入/离开状态
function dispatchVirtualMouseMove(x: number, y: number): DispatchMoveResult {
  // 更新虚拟鼠标位置
  currentVirtualMousePosition = { x, y }

  const targetElement = getElementAtPosition(x, y)
  const isOnUI = targetElement !== null

  dispatchVirtualPointerEvent(VIRTUAL_MOUSE_MOVE_EVENT, {
    x,
    y,
    button: 0,
    buttons: pressedButtons,
    isOnUI,
  })

  // 检测进入/离开远程桌面的状态变化
  const justEntered = !isOnUI && wasOnUIArea // 从 UI 区域离开到非 UI 区域，进入远程桌面
  const justLeave = isOnUI && !wasOnUIArea // 从非 UI 区域进入 UI 区域，离开远程桌面
  wasOnUIArea = isOnUI

  // 处理 mouseenter 和 mouseleave
  if (targetElement !== lastHoveredElement) {
    if (lastHoveredElement) {
      // 移除 hover class（用于替代无法触发的 CSS :hover）
      if (lastHoveredUIContainer) {
        lastHoveredUIContainer.classList.remove("virtual-hover")
        lastHoveredUIContainer = null
      }
      const leaveEvent = createSyntheticMouseEvent("mouseleave", x, y)
      lastHoveredElement.dispatchEvent(leaveEvent)
      const outEvent = createSyntheticMouseEvent("mouseout", x, y)
      lastHoveredElement.dispatchEvent(outEvent)
    }

    if (targetElement) {
      // 添加 hover class 到最近的 UI 容器元素（用于替代无法触发的 CSS :hover）
      const uiContainer = findUIContainer(targetElement)
      if (uiContainer) {
        uiContainer.classList.add("virtual-hover")
        lastHoveredUIContainer = uiContainer
      }
      const enterEvent = createSyntheticMouseEvent("mouseenter", x, y)
      targetElement.dispatchEvent(enterEvent)
      const overEvent = createSyntheticMouseEvent("mouseover", x, y)
      targetElement.dispatchEvent(overEvent)
    }

    lastHoveredElement = targetElement
  }

  // 派发 mousemove 事件
  if (targetElement) {
    const moveEvent = createSyntheticMouseEvent(
      "mousemove",
      x,
      y,
      pressedButtons
    )
    targetElement.dispatchEvent(moveEvent)
  }

  // 返回状态信息
  return { isOnUI, justEntered, justLeave }
}

// 派发虚拟鼠标 按下事件，返回是否作用在 UI 元素上
function dispatchVirtualMouseDown(
  x: number,
  y: number,
  button: number = 0
): boolean {
  const targetElement = getElementAtPosition(x, y)
  const isOnUI = targetElement !== null

  pressedButtons |= 1 << button
  dispatchVirtualPointerEvent(VIRTUAL_MOUSE_DOWN_EVENT, {
    x,
    y,
    button,
    buttons: pressedButtons,
    isOnUI,
  })

  if (targetElement) {
    // 派发 mousedown 事件到 UI 元素
    // 这对于需要 mousedown 事件的组件（如自定义滚动条）是必需的
    // 由于捕获阶段已经阻止了原生事件传播，合成事件不会触发 useOutsideClick
    const downEvent = createSyntheticMouseEvent(
      "mousedown",
      x,
      y,
      pressedButtons,
      button
    )
    targetElement.dispatchEvent(downEvent)

    mouseDownElement = targetElement
    // 保存 mousedown 时的 UI 容器，用于后续 click 事件判断
    mouseDownUIContainer = findUIContainer(targetElement)
    // 保存 mousedown 时的坐标，用于基于位置的 click 判断（解决 React 重渲染导致元素引用变化的问题）
    mouseDownPosition = { x, y }
    return true
  }
  if (!isOnUI) {
    pressedButtons &= ~(1 << button)
  }
  return false
}

// 派发虚拟鼠标 抬起事件，返回是否作用在 UI 元素上
function dispatchVirtualMouseUp(
  x: number,
  y: number,
  button: number = 0
): boolean {
  const targetElement = getElementAtPosition(x, y)
  const isOnUI = targetElement !== null

  pressedButtons &= ~(1 << button)
  dispatchVirtualPointerEvent(VIRTUAL_MOUSE_UP_EVENT, {
    x,
    y,
    button,
    buttons: pressedButtons,
    isOnUI,
  })

  if (targetElement) {
    const upEvent = createSyntheticMouseEvent(
      "mouseup",
      x,
      y,
      pressedButtons,
      button
    )
    targetElement.dispatchEvent(upEvent)

    // 基于位置的 click 判断（解决 React 重渲染导致元素引用变化的问题）
    // 如果 mousedown 和 mouseup 的位置距离在阈值内，则视为有效的点击
    const CLICK_THRESHOLD = 10 // 像素
    let isPositionClose = false
    if (mouseDownPosition !== null) {
      const dx = x - mouseDownPosition.x
      const dy = y - mouseDownPosition.y
      const distance = Math.sqrt(dx * dx + dy * dy)
      isPositionClose = distance <= CLICK_THRESHOLD
    }

    // 检查 mouseup 的元素是否在同一个 UI 容器内（而不是严格比较元素引用）
    // 这样可以避免因 React 重渲染导致元素引用变化而无法触发 click 的问题
    const currentUIContainer = findUIContainer(targetElement)
    const isSameContainer =
      currentUIContainer !== null &&
      mouseDownUIContainer !== null &&
      currentUIContainer === mouseDownUIContainer

    // 如果满足以下任一条件，派发 click 事件：
    // 1. 在同一个 UI 容器内
    // 2. 是同一个元素
    // 3. mousedown 和 mouseup 位置距离在阈值内（用于处理 React 重渲染导致元素引用变化的情况）
    if (
      isSameContainer ||
      targetElement === mouseDownElement ||
      isPositionClose
    ) {
      // 优先使用 mouseDownElement（如果仍在 DOM 中）
      // 这样即使 useOutsideClick 关闭了下拉列表，我们仍然可以点击正确的元素
      // 如果 mouseDownElement 已从 DOM 移除，则使用 targetElement
      let elementToClick: Element | null = null
      if (
        mouseDownElement &&
        document.body.contains(mouseDownElement) &&
        isFullScreenUIElement(mouseDownElement)
      ) {
        elementToClick = mouseDownElement
      } else {
        elementToClick = targetElement
      }

      // 找到可点击的 HTMLElement
      // 对于 SVG 等非 HTMLElement，需要找到最近的 HTMLElement 祖先
      let clickableElement: HTMLElement | null = null
      if (elementToClick instanceof HTMLElement) {
        clickableElement = elementToClick
      } else {
        // 对于非 HTMLElement（如 SVG 图标），找到最近的 HTMLElement 祖先
        let parent: Element | null = elementToClick.parentElement
        while (parent) {
          if (parent instanceof HTMLElement) {
            clickableElement = parent
            break
          }
          parent = parent.parentElement
        }
      }

      // 触发 click 事件
      if (clickableElement) {
        // 设置标志，告知后续原生 click 事件应该被阻止
        shouldBlockNextClick = true
        // 使用带有正确坐标的合成事件替代 .click()
        // .click() 会创建 clientX/clientY 为 0 的事件，导致依赖坐标的组件（如滚动条）行为异常
        const clickEvent = createSyntheticMouseEvent("click", x, y, 0, button)
        clickableElement.dispatchEvent(clickEvent)
      } else {
        // console.log("[VirtualMouse] 未找到可点击的 HTMLElement")
      }
    } else {
      // console.log("[VirtualMouse] 条件不满足，不触发 click")
    }
  }

  // 如果没有当前 UI 元素，但有 mouseDownElement 且仍在 DOM 中，也应该处理点击
  // 这处理了 useOutsideClick 关闭下拉列表导致 targetElement 为 null 的情况
  if (
    !targetElement &&
    mouseDownElement &&
    document.body.contains(mouseDownElement) &&
    isFullScreenUIElement(mouseDownElement)
  ) {
    let clickableElement: HTMLElement | null = null
    if (mouseDownElement instanceof HTMLElement) {
      clickableElement = mouseDownElement
    } else {
      let parent: Element | null = mouseDownElement.parentElement
      while (parent) {
        if (parent instanceof HTMLElement) {
          clickableElement = parent
          break
        }
        parent = parent.parentElement
      }
    }
    if (clickableElement) {
      shouldBlockNextClick = true
      // 使用带有正确坐标的合成事件
      const clickEvent = createSyntheticMouseEvent("click", x, y, 0, button)
      clickableElement.dispatchEvent(clickEvent)
    }
  }

  mouseDownElement = null
  mouseDownUIContainer = null
  mouseDownPosition = null
  // 返回是否有 UI 元素被命中
  return targetElement !== null
}

// 清理 hover 状态
function clearHoverState(): void {
  if (lastHoveredElement) {
    const leaveEvent = createSyntheticMouseEvent("mouseleave", 0, 0)
    lastHoveredElement.dispatchEvent(leaveEvent)
    lastHoveredElement = null
  }
  // 移除 hover class
  if (lastHoveredUIContainer) {
    lastHoveredUIContainer.classList.remove("virtual-hover")
    lastHoveredUIContainer = null
  }
  pressedButtons = 0
  mouseDownElement = null
  mouseDownUIContainer = null
  mouseDownPosition = null
  wasOnUIArea = false
}

// 查找可滚动的祖先元素（用于手动处理滚轮滚动）
// 也会检查兄弟元素（用于滚动条场景：滚动条和内容区域是兄弟元素）
function findScrollableAncestor(el: Element): Element | null {
  let current: Element | null = el
  while (current) {
    const style = window.getComputedStyle(current)
    const overflowY = style.overflowY
    const overflowX = style.overflowX

    // 检查是否可滚动
    const isScrollableY =
      (overflowY === "auto" || overflowY === "scroll") &&
      current.scrollHeight > current.clientHeight
    const isScrollableX =
      (overflowX === "auto" || overflowX === "scroll") &&
      current.scrollWidth > current.clientWidth

    if (isScrollableY || isScrollableX) {
      return current
    }

    // 检查兄弟元素是否可滚动（用于滚动条场景）
    const parentEl: Element | null = current.parentElement
    if (parentEl) {
      const childElements = parentEl.children
      for (let i = 0; i < childElements.length; i++) {
        const sibling: Element = childElements[i]
        if (sibling === current) continue
        const siblingStyle = window.getComputedStyle(sibling)
        const siblingOverflowY = siblingStyle.overflowY
        const siblingOverflowX = siblingStyle.overflowX
        const isSiblingScrollableY =
          (siblingOverflowY === "auto" || siblingOverflowY === "scroll") &&
          sibling.scrollHeight > sibling.clientHeight
        const isSiblingScrollableX =
          (siblingOverflowX === "auto" || siblingOverflowX === "scroll") &&
          sibling.scrollWidth > sibling.clientWidth
        if (isSiblingScrollableY || isSiblingScrollableX) {
          return sibling
        }
      }
    }

    current = current.parentElement
  }
  return null
}

// 创建合成滚轮事件
function createSyntheticWheelEvent(
  x: number,
  y: number,
  deltaX: number,
  deltaY: number,
  deltaMode: number = 0
): WheelEvent {
  return new WheelEvent("wheel", {
    bubbles: true,
    cancelable: true,
    view: window,
    clientX: x,
    clientY: y,
    screenX: x,
    screenY: y,
    deltaX,
    deltaY,
    deltaMode,
  })
}

// 派发虚拟鼠标 滚轮事件，返回是否作用在 UI 元素上
function dispatchVirtualMouseWheel(
  x: number,
  y: number,
  deltaX: number,
  deltaY: number
): boolean {
  const targetElement = getElementAtPosition(x, y)

  if (targetElement) {
    // 1. 派发合成事件（让 React 组件等可以监听到 wheel 事件）
    const wheelEvent = createSyntheticWheelEvent(x, y, deltaX, deltaY)
    targetElement.dispatchEvent(wheelEvent)

    // 2. 手动处理滚动：查找可滚动的祖先元素并执行滚动
    // 因为合成的 WheelEvent (isTrusted: false) 不会触发浏览器的原生滚动行为
    const scrollableAncestor = findScrollableAncestor(targetElement)
    if (scrollableAncestor) {
      scrollableAncestor.scrollBy({
        top: deltaY,
        left: deltaX,
        behavior: "auto",
      })
    }

    return true
  }
  return false
}

// 检查指定位置是否在 UI 元素上（用于判断是否需要阻止原生事件冒泡）
function isPositionOnUIElement(x: number, y: number): boolean {
  return getElementAtPosition(x, y) !== null
}

// 检查是否应该阻止下一个原生 click 事件（并重置标志）
function checkAndResetBlockClick(): boolean {
  const shouldBlock = shouldBlockNextClick
  shouldBlockNextClick = false
  return shouldBlock
}

// 导出虚拟鼠标事件工具
export const VirtualMouseEvents = {
  dispatchMove: dispatchVirtualMouseMove,
  dispatchDown: dispatchVirtualMouseDown,
  dispatchUp: dispatchVirtualMouseUp,
  dispatchWheel: dispatchVirtualMouseWheel,
  clearState: clearHoverState,
  getElementAtPosition,
  isPositionOnUI: isPositionOnUIElement,
  checkAndResetBlockClick,
  setupCaptureListeners,
  updatePosition: updateVirtualMousePosition,
}
